import { AlertTriangle, Info } from 'lucide-react';
import type { FocusEvent } from 'react';
import { offeredLanguages, type CountryConfig } from '../../shared/country/countryConfig';
import { useCountryStore } from '../../shared/country/countryStore';
import { authLegalLines } from '../../shared/country/marketCopy';
import { phonePlan } from '../../shared/country/phonePlan';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  LANGUAGE_LABELS,
  type Language,
  type MessageKey,
} from '../../shared/i18n/messages';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { BrandOwl, BrandWordmark } from '../../shared/brand/BrandOwl';
export { validateUsername } from '../../shared/username/validateUsername';

/**
 * Pure helpers and small presentational bits shared by `AuthPage` and the
 * three split-out forms (`LoginForm` / `RegisterForm` / `ForgotForm`) plus
 * `useAuthIdentifier` / `useOtpChallenge`.
 *
 * Pulled out of `AuthPage.tsx` (post-Task-9 cleanup): every consumer used to
 * import these from `./AuthPage`, while `AuthPage` imported the three forms
 * — a real import cycle. It happened to work only because every shared
 * symbol here is a hoisted `function` used purely at render time; a single
 * module-scope `const x = maskIdentifier(...)` anywhere in the cycle would
 * have hit the TDZ. Moving the shared surface to a leaf module (no imports
 * from any auth form) removes the cycle rather than relying on it staying
 * accidentally safe.
 */

export function AuthLangBar() {
  const language = useI18nStore((state) => state.language);
  const setLanguage = useI18nStore((state) => state.setLanguage);
  const availableLocales = useCountryStore((state) => state.country.availableLocales);
  // Only the languages this market offers, narrowed to what the bundle ships.
  const offered = offeredLanguages(availableLocales, LANGUAGES, DEFAULT_LANGUAGE);
  return (
    <div className="auth-lang-bar">
      {offered.map((lang) => (
        <button
          key={lang}
          type="button"
          className={`auth-lang-pill tap${language === lang ? ' auth-lang-active' : ''}`}
          onClick={() => setLanguage(lang as Language)}
        >
          {/* LANGUAGE_LABELS rather than a ternary chain: the old
              `lang === 'pt-BR' ? 'PT' : lang === 'en' ? 'EN' : 'ES'` labelled every
              unknown language 'ES', so adding a fourth would have shipped a pill
              reading ES to a market that does not speak Spanish. */}
          {LANGUAGE_LABELS[lang]}
        </button>
      ))}
    </div>
  );
}

export function BrandMini() {
  return (
    <div className="brand-mini" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <BrandOwl size={22} />
      <BrandWordmark height={15} />
    </div>
  );
}

export function maskIdentifier(identifier: string): string {
  const value = identifier.trim();
  if (value.includes('@')) {
    const [user, domain] = value.split('@');
    const head = user.slice(0, 1);
    const tail = user.length > 2 ? user.slice(-1) : '';
    return `${head}•••${tail}@${domain}`;
  }
  const digits = value.replace(/\D/g, '');
  if (digits.length >= 4) return `•••• ${digits.slice(-4)}`;
  return value;
}

export function ErrLine({ text, className = 'mt-2' }: { text: string; className?: string }) {
  return (
    <p className={`text-xs text-rose-400 ${className} inline-flex items-center gap-1.5`}>
      <AlertTriangle size={14} className="shrink-0" aria-hidden="true" />
      <span>{text}</span>
    </p>
  );
}

export function HintLine({ text, tone = 'emerald' }: { text: string; tone?: 'emerald' | 'amber' }) {
  const color = tone === 'amber' ? 'text-amber-400/80' : 'text-emerald-300/90';
  return (
    <p className={`text-[11px] ${color} mt-1.5 ml-1 inline-flex items-start gap-1.5`}>
      <Info size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span>{text}</span>
    </p>
  );
}

export function LegalFooter({ t, showResponsible = true }: { t: ReturnType<typeof useI18nStore.getState>['t']; showResponsible?: boolean }) {
  return (
    <>
    {showResponsible ? <p className="ds-soul mt-4">{t('soulQuote')}</p> : null}
    <div className="flex justify-center items-center gap-2 mt-2 text-[10px] text-white/35">
      <a href="/termos.html" target="_blank" rel="noopener" className="hover:text-white/60 tap">{t('legalTerms')}</a>
      <span className="text-white/20">·</span>
      <a href="/privacidade.html" target="_blank" rel="noopener" className="hover:text-white/60 tap">{t('legalPrivacy')}</a>
      <span className="text-white/20">·</span>
      {showResponsible ? (
        <>
          <a href="/responsavel.html" target="_blank" rel="noopener" className="hover:text-white/60 tap">{t('legalResponsible')}</a>
          <span className="text-white/20">·</span>
        </>
      ) : null}
      <span className="text-gold-400/80 font-bold">{t('legalAge')}</span>
    </div>
    </>
  );
}

/**
 * The legal lines under an auth screen, following the country the screen acts as.
 *
 * Brazil: its registered company, then the domain. Everyone else: the domain
 * with their own endonym. The lines themselves come from `authLegalLines`; this
 * only subscribes to the stores so the footer re-renders when the player picks a
 * country or the account's country is adopted after sign-in.
 */
export function AuthLegalFoot({ className = 'text-center text-[10px] text-white/25 mt-1 leading-snug' }: { className?: string }) {
  const t = useI18nStore((state) => state.t);
  const country = useCountryStore((state) => state.country);
  return (
    <>
      {authLegalLines(country, t).map((line) => <p key={line} className={className}>{line}</p>)}
    </>
  );
}

export function scrollAuthInputIntoView(event: FocusEvent<HTMLElement>) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return;
  window.setTimeout(() => {
    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      target.scrollIntoView();
    }
  }, 300);
}

export function normalizeIdentifierInput(raw: string, country: CountryConfig) {
  const mode = identifierInputMode(raw);
  if (mode !== 'phone') return raw;
  const plan = phonePlan(country);
  const digits = plan.normalize(raw, country);
  return digits ? plan.format(digits, country) : '';
}

export function identifierInputMode(raw: string): 'phone' | 'email' | 'username' | 'unknown' {
  const value = raw.trim();
  if (!value) return 'unknown';
  if (value.includes('@')) return 'email';
  if (/[A-Za-z]/.test(value)) return /^[A-Za-z][A-Za-z0-9._]{0,31}$/.test(value) ? 'username' : 'email';
  if (/^\(/.test(value)) return 'phone';
  // Legacy waits for ≥3 digits before flipping to phone mode (+55 badge + mask).
  return value.replace(/\D/g, '').length >= 3 ? 'phone' : 'unknown';
}

/**
 * Render a country's phone hint.
 *
 * The plan returns a key plus params rather than finished copy so it stays pure
 * and testable — `t` lives only here.
 */
export function phoneHintText(
  raw: string,
  country: CountryConfig,
  t: ReturnType<typeof useI18nStore.getState>['t'],
  opts?: { mobileOnly?: boolean },
) {
  const hint = phonePlan(country).hint(raw, country, opts);
  if (!hint) return '';
  let text = t(hint.key);
  for (const [token, value] of Object.entries(hint.params || {})) text = text.replace(token, value);
  return text;
}

export function identifierOtpEligible(raw: string, country: CountryConfig) {
  const value = raw.trim();
  if (!value) return false;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return true;
  return phonePlan(country).isValid(value, country);
}

export function normalizeOtpExpiresAt(_payload: { expires_at?: number; expiresAt?: number; expires_in?: number; expiresIn?: number }) {
  // Legacy always shows a fixed 10:00 validity window after sending a code.
  return Date.now() + 10 * 60 * 1000;
}

export function errorPayload(error: unknown) {
  if (error && typeof error === 'object' && 'payload' in error) return (error as { payload?: unknown }).payload;
  return null;
}

export function errorRetryAfterMs(error: unknown) {
  const payload = errorPayload(error);
  if (payload && typeof payload === 'object' && 'retry_after_ms' in payload) return Number((payload as { retry_after_ms?: number }).retry_after_ms || 0);
  return 0;
}

export function errorReason(error: unknown) {
  const payload = errorPayload(error);
  if (payload && typeof payload === 'object' && 'reason' in payload) return String((payload as { reason?: string }).reason || '');
  return '';
}

export function loginOtpLockoutLabel(until: number, reason: string, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (!until || Date.now() >= until) return '';
  const minutes = Math.max(1, Math.ceil((until - Date.now()) / 60_000));
  if (reason === 'daily') return t('loginOtpLockedDaily');
  if (reason === 'ip') return t('loginOtpLockedIp');
  return t('loginOtpLockedHourly').replace('%min%', String(minutes));
}

export function formatCountdown(totalSeconds: number) {
  // Legacy zero-pads the minutes too ("09:59").
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// Legacy persists {identifier, remember} in localStorage `sorteia:auth` so a
// returning user gets a prefilled identifier and the "Welcome back" heading.
const AUTH_PREFS_KEY = 'sorteia:auth';

export function readAuthPrefs(): { identifier: string; remember: boolean } {
  if (typeof window === 'undefined') return { identifier: '', remember: false };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(AUTH_PREFS_KEY) || 'null') as { identifier?: unknown; remember?: unknown } | null;
    if (parsed && typeof parsed === 'object') {
      return { identifier: parsed.remember === true ? String(parsed.identifier || '') : '', remember: parsed.remember === true };
    }
  } catch {
    // ignore storage/parse errors
  }
  return { identifier: '', remember: false };
}

export function writeAuthPrefs(identifier: string, remember: boolean) {
  if (typeof window === 'undefined') return;
  try {
    if (remember) window.localStorage.setItem(AUTH_PREFS_KEY, JSON.stringify({ identifier, remember: true }));
    else window.localStorage.removeItem(AUTH_PREFS_KEY);
  } catch {
    // ignore storage errors
  }
}

// Legacy maps backend error codes to localized copy (error-messages-feature.js).
export function authErrorMessage(error: unknown, t: ReturnType<typeof useI18nStore.getState>['t'], identifierMode: string, fallback: MessageKey) {
  const code = error instanceof Error ? error.message : '';
  // AuthController never sends INVALID_PHONE — the identifier-shape rejection
  // it actually returns (register's phone branch, and every other
  // isEmpty()-on-AuthIdentifier guard) is INVALID_IDENTIFIER. This code path
  // was unreachable dead mapping (final review, item 2).
  if (code === 'INVALID_IDENTIFIER') return t(identifierMode === 'email' ? 'errIdentifierInvalid' : 'errInvalidPhone');
  if (code === 'INVALID_OTP') return t('errOtpDemo');
  if (code === 'otp_invalid') return t('authOtpLoginFail');
  if (code === 'otp_expired') return t('authOtpExpired');
  if (code === 'INVALID_PASSWORD') return t('passwordRuleHint');
  if (code === 'PHONE_TAKEN') return t('authPhoneTaken');
  if (code === 'EMAIL_TAKEN') return t('authEmailTaken');
  if (code === 'INVITE_INVALID') return t('authInviteInvalid');
  if (code === 'USER_NOT_FOUND') return t('authAccountNotFound');
  if (code === 'INVALID_CREDENTIALS') return t('authPasswordIncorrect');
  if (code === 'channel_not_verified') return t('errChannelNotVerified');
  if (code === 'no_verified_channel') return t('errNoVerifiedChannel');
  // Register-only codes (backend AuthController.register, Task 6/7): the OTP
  // spent at submit time was wrong/expired, or the verified number's prefix
  // does not belong to any active market. Neither had client copy before
  // Task 10 — a player would have seen the raw code as the error text.
  if (code === 'PASSWORD_MISMATCH') return t('passwordsMismatch');
  if (code === 'VERIFICATION_FAILED' || code === 'VERIFICATION_UNAVAILABLE') return t('verificationRetry');
  if (code === 'OTP_INVALID') return t('errOtpInvalid');
  if (code === 'COUNTRY_NOT_SUPPORTED') return t('errCountryNotSupported');
  // Final-review gap (item 1): these three backend codes had zero client
  // copy and fell through to the generic `fallback` text below.
  if (code === 'OTP_SMS_UNAVAILABLE') return t('errOtpSmsUnavailable');
  if (code === 'otp_rate_limited') return t('errOtpRateLimited');
  return t(fallback);
}
